import React from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';

import { IUserProfileStatsRes } from 'modules/cabinet/models';
import { globalConst } from 'modules/global';

const ResponsiveLine = dynamic(() => import('@nivo/line').then(mod => mod.ResponsiveLine), {
  ssr: false
});

interface IDataPoint {
  x: string;
  y: number;
}

interface IChartData {
  id: string;
  data: IDataPoint[];
  color: string;
}

interface IProps {
  userStats: IUserProfileStatsRes[] | undefined;
}

const ResponsiveLineChart = ({ userStats }: IProps) => {
  const t = useTranslations();
  const onlyOneMineTrue = userStats?.length === 1 && userStats[0].mine === true;

  const transformedData: IChartData[] = (userStats || []).map(statsItem => {
    const dataPoints =
      statsItem.results?.map(item => ({
        x: String(item.short_name),
        y: Number(item.ball)
      })) || [];

    const isMine = statsItem.mine;
    const id = isMine ? 'You' : 'Other';

    let color = globalConst.colors.primary; // default

    if (!onlyOneMineTrue) {
      color = isMine ? globalConst.colors.gray[400] : globalConst.colors.primary;
    }

    return {
      id,
      data: dataPoints,
      color
    };
  });

  // Flatten all points to find the maxY
  const allYValues = transformedData.flatMap(series => series.data.map(point => point.y));
  const hasOnlyZeros = allYValues.every(y => y === 0);
  const maxY = hasOnlyZeros ? 10 : Math.max(...allYValues);

  return (
    <ResponsiveLine
      data={transformedData.map(({ id, data }) => ({ id, data }))}
      margin={{ top: 10, right: 10, bottom: 30, left: 30 }}
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 0,
        max: maxY
      }}
      curve="linear"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0
      }}
      colors={transformedData.map(item => item.color)}
      pointSize={10}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      enablePointLabel={false}
      enableGridX={false}
      enableGridY={true}
      lineWidth={3}
      enableArea={false}
      useMesh={true}
      tooltip={({ point }) => {
        const foundItem = userStats
          ?.flatMap(item => item.results || [])
          .find(item => String(item.short_name) === point.data.x);

        return (
          <div
            style={{
              background: 'white',
              padding: '8px',
              borderRadius: '4px',
              boxShadow: '0px 2px 10px rgba(0,0,0,0.15)',
              color: '#333'
            }}
          >
            <strong>{String(foundItem?.name || point.data.x)}</strong>
            <br />
            {t('ball').toLowerCase()}: <strong>{point.data.yFormatted}</strong>
          </div>
        );
      }}
      theme={{
        axis: {
          ticks: {
            text: {
              fontSize: 12,
              fill: '#777777'
            }
          }
        },
        grid: {
          line: {
            stroke: '#dddddd',
            strokeWidth: 1
          }
        }
      }}
    />
  );
};

export default ResponsiveLineChart;
